import React from "react";
import PropTypes from "prop-types";
import { TableRow, TableCell, Chip } from "@mui/material";
import { dotNotationExtractor, isFunction } from "utils/functions";
import { formatter } from "constantes/constants";
import { getStatusLabel } from "utils/formHelpers";
import { useTheme } from '@mui/material/styles';

function Row({ columns, data = {}, stripe, index, visibleColumns, columnWidths, rowStyle }) {
  const theme = useTheme();

  const backgroundColor =
    (!stripe && index % 2 !== 0)
      ? theme.palette.action.hover
      : 'transparent';

  // Aplicar estilo personalizado si existe
  const customStyle = rowStyle && typeof rowStyle === 'function' ? rowStyle(data) : {};
  const finalStyle = { backgroundColor, ...customStyle };

  return (
    <TableRow style={finalStyle}>
      {columns
        .filter(col => !visibleColumns || visibleColumns.includes(col.name) || !col.name)
        .map((col, index) => {
          // Lógica mejorada para manejar campos y componentes
          let cellContent;

          // Validar que la columna tenga un name definido
          if (!col.name) {
            // Si no hay name, solo mostrar el componente si existe
            if (col.component) {
              cellContent = isFunction(col.component) ? col.component(data) : col.component;
            } else {
              cellContent = null; // No mostrar nada si no hay name ni component
            }
          } else if (col.component) {
            // Si hay un componente, usar el campo especificado o intentar inferir el campo original
            if (col.field) {
              // Usar el campo explícitamente especificado
              cellContent = dotNotationExtractor(data, col.field);
            } else if (col.name && col.name.includes('_')) {
              // Si el name es un alias (contiene _), intentar inferir el campo original
              // Por ejemplo: status_alias -> status, third_party_name -> third_party.name
              const originalField = col.name.replace(/_alias$/, '').replace(/_/g, '.');
              cellContent = dotNotationExtractor(data, originalField);
            } else {
              // Usar el name directamente
              cellContent = dotNotationExtractor(data, col.name);
            }
            // Aplicar el componente PRIMERO (antes del formateo de booleanos)
            const componentResult = isFunction(col.component) ? col.component(data) : col.component;

            // Si el componente retorna un elemento React, usarlo directamente
            // Si retorna un valor primitivo, aplicar formateo
            if (React.isValidElement(componentResult)) {
              cellContent = componentResult;
            } else {
              cellContent = componentResult;

              // Aplicar formateo solo si hay contenido y no es un elemento React
              if (cellContent !== null && cellContent !== undefined) {
                cellContent =
                  col.type === "number"
                    ? formatter.format(cellContent ?? 0)
                    : cellContent;

                // Manejar campos booleanos automáticamente SOLO si no hay componente personalizado
                if (col.filterType === 'boolean' && !col.component) {
                  // Convertir cualquier valor a booleano de manera robusta
                  // Compatible con campos bool (true/false) y tinyint (0/1)
                  let booleanValue = false;

                  if (typeof cellContent === 'boolean') {
                    // Valor ya es booleano
                    booleanValue = cellContent;
                  } else if (typeof cellContent === 'number') {
                    // Valor numérico (0, 1, etc.)
                    booleanValue = Boolean(cellContent);
                  } else if (typeof cellContent === 'string') {
                    // Valor string ("0", "1", "true", "false", etc.)
                    const trimmedValue = cellContent.trim().toLowerCase();
                    booleanValue = ['1', 'true', 'yes', 'sí', 'on', 'active'].includes(trimmedValue);
                  } else if (cellContent === null || cellContent === undefined) {
                    // Valor nulo o indefinido
                    booleanValue = false;
                  } else {
                    // Otros tipos (array, object, etc.)
                    booleanValue = Boolean(cellContent);
                  }

                  const booleanOptions = col.booleanOptions || { true: 'Sí', false: 'No' };
                  const displayValue = booleanOptions[booleanValue] || cellContent;

                  // Renderizar chip automáticamente
                  const chipColor = booleanValue ? 'success' : 'error';
                  cellContent = (
                    <Chip
                      label={displayValue}
                      color={chipColor}
                      size="small"
                      variant="outlined"
                    />
                  );
                } else {
                  cellContent =
                    col.name === "status" ? getStatusLabel(cellContent) : cellContent;
                }
              }
            }
          } else {
            // Sin componente, usar el name directamente
            cellContent = dotNotationExtractor(data, col.name);

            // Aplicar formateo solo si hay contenido
            if (cellContent !== null && cellContent !== undefined) {
              cellContent =
                col.type === "number"
                  ? formatter.format(cellContent ?? 0)
                  : cellContent;

              // Manejar campos booleanos automáticamente
              if (col.filterType === 'boolean') {
                // Convertir cualquier valor a booleano de manera robusta
                // Compatible con campos bool (true/false) y tinyint (0/1)
                let booleanValue = false;

                if (typeof cellContent === 'boolean') {
                  // Valor ya es booleano
                  booleanValue = cellContent;
                } else if (typeof cellContent === 'number') {
                  // Valor numérico (0, 1, etc.)
                  booleanValue = Boolean(cellContent);
                } else if (typeof cellContent === 'string') {
                  // Valor string ("0", "1", "true", "false", etc.)
                  const trimmedValue = cellContent.trim().toLowerCase();
                  booleanValue = ['1', 'true', 'yes', 'sí', 'on', 'active'].includes(trimmedValue);
                } else if (cellContent === null || cellContent === undefined) {
                  // Valor nulo o indefinido
                  booleanValue = false;
                } else {
                  // Otros tipos (array, object, etc.)
                  booleanValue = Boolean(cellContent);
                }

                const booleanOptions = col.booleanOptions || { true: 'Sí', false: 'No' };
                const displayValue = booleanOptions[booleanValue] || cellContent;

                // Renderizar chip automáticamente
                const chipColor = booleanValue ? 'success' : 'error';
                cellContent = (
                  <Chip
                    label={displayValue}
                    color={chipColor}
                    size="small"
                    variant="outlined"
                  />
                );
              } else {
                cellContent =
                  col.name === "status" ? getStatusLabel(cellContent) : cellContent;
              }
            }
          }

          const columnWidth = columnWidths[index] || col.width || 150;
          return (
            <TableCell
              key={`Col${String(index)}`}
              align={col.align || "left"}
              style={{ width: columnWidth }}
            >
              {cellContent}
            </TableCell>
          );
        })}
    </TableRow>
  );
}

Row.propTypes = {
  columns: PropTypes.oneOfType([PropTypes.array]).isRequired,
  data: PropTypes.oneOfType([PropTypes.object]),
  stripe: PropTypes.oneOf(['odd', 'even']),
  index: PropTypes.number.isRequired,
  visibleColumns: PropTypes.arrayOf(PropTypes.string),
  columnWidths: PropTypes.object,
  rowStyle: PropTypes.func,
};

export default Row;
